import { createCrowdExpression } from 'api/crowdExpressions';
import Toast from 'components/ui/Toast';
import { useToast } from 'hooks/useToast';
import React, { useState, useRef, useEffect } from 'react';
import { formatDuration } from 'utils/formatDuration';

const RecordringForm = ({ }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    city: '',
    phone: '',
  });

  const recordedAudioRef = useRef(null);
  const recordingStartRef = useRef(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState(null);
  const [recordedAudioBlob, setRecordedAudioBlob] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(null);
  const [recordingElapsed, setRecordingElapsed] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const { toast, showToast, hideToast } = useToast(3000);

  const onInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      showToast('مرورگر شما از ضبط صدا پشتیبانی نمی‌کند.', 'error');
      return;
    }

    try {
      recordingStartRef.current = Date.now();
      setRecordedAudioUrl(null);
      setRecordedAudioBlob(null);
      setRecordingDuration(null);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      recorder.onstop = () => {
        const durationInSeconds =
          (Date.now() - recordingStartRef.current) / 1000;
        setRecordingDuration(durationInSeconds);

        const blob = new Blob(chunks, { type: 'audio/webm' });
        setRecordedAudioBlob(blob);
        setRecordedAudioUrl(URL.createObjectURL(blob));
      };

      recorder.start();
      setMediaRecorder(recorder);
      setRecording(true);
    } catch (err) {
      console.log('Could not start recording: ' + err.message);
      showToast(`مشکل در ضبط صدا: ${err.message}`, 'error');
    }
  };

  const stopRecording = () => {
    if (!mediaRecorder) return;

    mediaRecorder.stop();
    mediaRecorder.stream.getTracks().forEach((track) => track.stop());
    setRecording(false);
  };

  const onSubmit = async (e) => {
    setSubmitting(true);
    e.preventDefault();
    if (!recordedAudioBlob) {
      showToast('لطفا برای ثبت، صدای خود را ضبط کنید.', 'error');
      setSubmitting(false);
      return;
    }
    if (
      recordingDuration == null ||
      recordingDuration <= 5 ||
      recordingDuration >= 30
    ) {
      showToast(
        'مدت زمان فایل صوتی باید بیشتر از ۵ ثانیه و کمتر از ۳۰ ثانیه باشد.',
        'error',
      );
      setSubmitting(false);
      return;
    }
    try {
      await createCrowdExpression(
        formData.firstName,
        formData.city,
        recordingDuration,
        recordedAudioBlob,
      );
      showToast('صدا با موفقیت ثبت شد.', 'success');
    } catch (err) {
      showToast(err.message || 'خطا در ثبت فرم', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (recordedAudioRef.current && recordedAudioUrl) {
      recordedAudioRef.current.load();
    }
  }, [recordedAudioUrl]);
  useEffect(() => {
    if (!recording || recordedAudioRef === null) {
      setRecordingElapsed(0);
      return;
    }

    const update = () => {
      setRecordingElapsed((Date.now() - recordingStartRef.current) / 1000);
    };

    update();
    const timer = setInterval(update, 1000);

    return () => clearInterval(timer);
  }, [recording, recordingStartRef]);
  return (
    <>
      <Toast
        visible={toast.visible}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />

      <form
        onSubmit={onSubmit}
        className="w-full max-w-xl p-5 sm:p-6 flex flex-col gap-4 overflow-hidden rounded-2xl border transition-all duration-200 bg-[rgba(29,38,51,0.92)] border-[rgba(255,255,255,0.08)] text-[#e8eef7] shadow-[0_8px_30px_rgba(0,0,0,0.24)]"
      >
        <div className="space-y-1">
          <h3 className="m-0 text-lg font-semibold text-[#e8eef7]">
            به یاد ماکان نصیر از متن زیر بخونید
          </h3>
          <p className="m-0 text-sm text-[#aeb9c8]">
            سلام مامان ماکان، ماکان در قلب ماست، منم ماکانم، مهرداد از تهران
          </p>
        </div>

        <input
          type="text"
          name="firstName"
          placeholder="نام"
          value={formData.firstName}
          onChange={onInputChange}
          required
          className="w-full rounded-xl px-4 py-3 outline-none transition-all duration-200 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-[#e8eef7] placeholder:text-[#aeb9c8] focus:border-[rgba(255,255,255,0.2)]"
        />

        <input
          type="text"
          name="city"
          placeholder="شهر"
          value={formData.city}
          onChange={onInputChange}
          required
          className="w-full rounded-xl px-4 py-3 outline-none transition-all 
          duration-200 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-[#e8eef7] placeholder:text-[#aeb9c8] focus:border-[rgba(255,255,255,0.2)]"
        />

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={recording ? stopRecording : startRecording}
            className={`inline-flex items-center justify-center rounded-full px-4 py-3 text-sm
            font-medium transition-all duration-200 hover:-translate-y-px 
            active:scale-[0.97] bg-[rgba(255,255,255,0.05)] 
            border border-[rgba(255,255,255,0.1)] text-[#e8eef7] gap-2
            ${recording ? 'bg-[rgba(255,255,255,0.14)]' : ''}`}
          >
            {recording && (
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
            )}
            {recording ? 'توقف ضبط' : 'شروع ضبط '}
          </button>
          {recording && (
            <p className="m-0 text-sm text-[#aeb9c8]">
              {formatDuration(recordingElapsed)}
            </p>
          )}

          {recordedAudioUrl && (
            <audio
              controls
              src={recordedAudioUrl}
              ref={recordedAudioRef}
              className="w-full rounded-xl bg-[rgba(255,255,255,0.05)]"
            />
          )}
        </div>

        <button
          type="submit"
          className="mt-2 px-6 py-3 font-semibold inline-flex items-center justify-center rounded-full text-sm transition-all duration-200 hover:-translate-y-px active:scale-[0.97] bg-white text-black"
          disabled={submitting}
        >
          {submitting ? 'در حال ارسال...' : 'به یاد ماکان ثبت شود'}
        </button>
      </form>
    </>
  );
};

export default RecordringForm;
